import { QpLoggerService } from '@library/services/qp-logger/qp-logger.service';
import { inject, Injectable } from '@angular/core';

/**
 * @description
 * Asks the browser to make this origin's storage persistent.
 *
 * Without it, Android evicts Cache Storage and IndexedDB under storage pressure. For the service worker that
 * means the control table or the cached shell can disappear, and the next cold start while offline ends in a
 * `504` because `initialize()` falls back to fetching the manifest over the network. It would also drop the
 * Dexie inspection data an inspector is working on.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/StorageManager/persist
 */
@Injectable({ providedIn: 'root' })
export class SwStoragePersistenceService {
  public readonly _qpLoggerService = inject(QpLoggerService);

  public init(): void {
    void this._requestPersistentStorage();
  }

  private async _requestPersistentStorage(): Promise<void> {
    if (!navigator.storage?.persist || !navigator.storage.persisted) {
      return;
    }

    try {
      if (await navigator.storage.persisted()) {
        return;
      }

      const isPersisted: boolean = await navigator.storage.persist();

      if (isPersisted) {
        this._qpLoggerService.info('💾 Persistent storage granted');
      } else {
        this._qpLoggerService.warn('💾 Persistent storage denied, offline data and app shell may be evicted');
      }
    } catch (error: unknown) {
      this._qpLoggerService.error(error);
    }
  }
}
